import React from 'react';

const regimeStyles = {
  GREEN: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.08)', border: 'rgba(34, 197, 94, 0.24)' },
  YELLOW: { color: '#eab308', bg: 'rgba(234, 179, 8, 0.08)', border: 'rgba(234, 179, 8, 0.24)' },
  ORANGE: { color: '#f97316', bg: 'rgba(249, 115, 22, 0.08)', border: 'rgba(249, 115, 22, 0.24)' },
  RED: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.24)' },
};

function getRegime(signal, score) {
  const safeScore = Number.isFinite(Number(score)) ? Math.round(Number(score)) : 50;
  if (safeScore <= 24) {
    return { key: 'ALL_CLEAR', label: 'ALL CLEAR', min: 0, max: 24, nextTrigger: 25, signal: 'GREEN' };
  }
  if (safeScore <= 49) {
    return { key: 'CAUTION', label: 'CAUTION', min: 25, max: 49, nextTrigger: 50, signal: 'YELLOW' };
  }
  if (safeScore <= 74) {
    return { key: 'ELEVATED', label: 'ELEVATED RISK', min: 50, max: 74, nextTrigger: 75, signal: 'ORANGE' };
  }
  return { key: 'DANGER', label: 'DANGER', min: 75, max: 100, nextTrigger: null, signal: 'RED' };
}

export default function RiskRegimeBoundaries({ signal, score, exactScore, riskRegime, rawWeightedScore, breadthFloorApplied, riskBreadth }) {
  const regime = riskRegime || getRegime(signal, score);
  const style = regimeStyles[regime.signal || signal] || regimeStyles.GREEN;
  const displayedScore = Number.isFinite(Number(score)) ? Math.round(Number(score)) : 0;
  const displayedExact = Number.isFinite(Number(exactScore)) ? Number(exactScore).toFixed(2) : null;
  const scoreIntoRegime = Math.max(0, displayedScore - regime.min);
  const regimeWidth = Math.max(1, regime.max - regime.min + 1);
  const progress = Math.max(0, Math.min(100, (scoreIntoRegime / regimeWidth) * 100));

  return (
    <div className="glass-card p-4 border animate-slide-up" style={{ borderColor: style.border, backgroundColor: style.bg }}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs text-mentor-muted font-display uppercase tracking-widest mb-1">
            Risk Regime Boundary
          </p>
          <h4 className="font-display font-bold text-base" style={{ color: style.color }}>
            Current Regime: {regime.label}
          </h4>
          <p className="text-sm text-mentor-text/85 font-body mt-1">
            Current Score: <span className="font-mono font-semibold">{displayedScore}/100</span>
            {displayedExact && (
              <span className="text-mentor-muted"> · Exact decision score: {displayedExact}</span>
            )}
          </p>
          {Number.isFinite(Number(rawWeightedScore)) && (
            <p className="text-xs text-mentor-muted font-body mt-1">
              Raw weighted score: <span className="font-mono">{Number(rawWeightedScore).toFixed(2)}</span>
              {Number.isFinite(Number(breadthFloorApplied)) && (
                <span> · Breadth guardrail applied: <span className="font-mono">{Number(breadthFloorApplied).toFixed(0)}+</span></span>
              )}
            </p>
          )}
          {riskBreadth?.cautionOrWorse >= 4 && (
            <p className="text-xs text-mentor-muted font-body mt-1">
              Breadth check: {riskBreadth.cautionOrWorse}/{riskBreadth.total} components are caution-or-worse, so the system avoids an “All Clear” conflict.
            </p>
          )}
        </div>

        <div className="md:min-w-[280px]">
          <div className="flex justify-between text-xs text-mentor-muted font-mono mb-1">
            <span>{regime.min}</span>
            <span>{regime.max}</span>
          </div>
          <div className="h-2.5 bg-mentor-bg rounded-full border border-mentor-border overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.max(progress, 4)}%`, backgroundColor: style.color, boxShadow: `0 0 10px ${style.color}55` }}
            />
          </div>
          <p className="text-xs text-mentor-muted font-body mt-2 text-right">
            {regime.nextTrigger === null
              ? 'Highest warning band already active.'
              : `Next Regime Trigger: ${regime.nextTrigger}+`}
          </p>
        </div>
      </div>
    </div>
  );
}
